import React, { useEffect, useState } from "react";
import { BellRing, Loader2, CheckCircle2, XCircle, AlertTriangle, Smartphone } from "lucide-react";
import api from "../lib/api";
import { pushSupported, enablePush } from "../lib/push";

const permLabel = (p, ta) => p === "granted" ? (ta ? "அனுமதி உள்ளது" : "Allowed") : p === "denied" ? (ta ? "Block ஆகியுள்ளது" : "Blocked") : (ta ? "இன்னும் கேட்கவில்லை" : "Not asked yet");

/* Settings → "Send Me a Test Alert": permission check → (re)subscribe → real server push → per-device delivery status. */
export const PushTestCard = ({ ta = false, dark = false }) => {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [subbed, setSubbed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [arrived, setArrived] = useState(false);

  const refresh = async () => {
    if (!pushSupported()) { setSubbed(false); return; }
    try { const reg = await navigator.serviceWorker.ready; setSubbed(!!(await reg.pushManager.getSubscription())); } catch { setSubbed(false); }
    setPerm(Notification.permission);
  };
  useEffect(() => {
    refresh();
    const onMsg = (e) => { if (e.data?.type === "PUSH_RECEIVED" && e.data.tag === "test") setArrived(true); };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMsg);
  }, []);

  const send = async () => {
    setBusy(true); setRes(null); setErr(""); setArrived(false);
    try {
      if (!pushSupported()) throw new Error(ta ? "இந்த browser push notification-ஐ support செய்யவில்லை. Chrome-ல் திறந்து Install App செய்யவும்." : "This browser does not support push. Open in Chrome and Install App.");
      if (Notification.permission === "denied") throw new Error(ta ? "Notification block ஆகியுள்ளது. Chrome → ⋮ → Site settings → Notifications → Allow செய்து மீண்டும் முயலவும்." : "Notifications are blocked. Chrome → ⋮ → Site settings → Notifications → Allow, then retry.");
      await enablePush();
      await refresh();
      const { data } = await api.post("/push/test");
      setRes(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : e.message || "Failed");
      await refresh();
    } finally { setBusy(false); }
  };

  const okPerm = perm === "granted";
  const box = dark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`rounded-2xl border p-3 ${box}`} data-testid="push-test-card">
      <div className="flex items-center gap-2">
        <BellRing className="w-4.5 h-4.5 text-amber-500" />
        <div className="flex-1">
          <div className="text-sm font-bold">{ta ? "Test Alert அனுப்பு" : "Send Me a Test Alert"}</div>
          <div className={`text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>{ta ? "உங்கள் phone-க்கு உண்மையான push notification வரும்" : "A real push notification is sent to this logged-in device"}</div>
        </div>
        <button onClick={send} disabled={busy} className="rl-gold-bg text-[11px] font-black px-3 py-2 rounded-full disabled:opacity-60 active:scale-95 transition-transform flex items-center gap-1" data-testid="push-test-send-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}{busy ? (ta ? "அனுப்புகிறது…" : "Sending…") : (ta ? "அனுப்பு" : "Send")}
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5 text-[10px]">
        <div className={`rounded-lg px-2 py-1.5 flex items-center gap-1 ${okPerm ? "bg-emerald-50 text-emerald-700" : perm === "denied" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`} data-testid="push-test-permission" data-perm={perm}>
          {okPerm ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}{ta ? "அனுமதி" : "Permission"}: <b>{permLabel(perm, ta)}</b>
        </div>
        <div className={`rounded-lg px-2 py-1.5 flex items-center gap-1 ${subbed ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="push-test-subscription" data-subbed={subbed ? "1" : "0"}>
          <Smartphone className="w-3 h-3" />{ta ? "இந்த phone" : "This device"}: <b>{subbed === null ? "…" : subbed ? (ta ? "இணைந்தது" : "Subscribed") : (ta ? "இணைக்கவில்லை" : "Not subscribed")}</b>
        </div>
      </div>
      {err && <div className="mt-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[11px] p-2 flex gap-1.5" data-testid="push-test-error"><XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{err}</span></div>}
      {res && (
        <div className={`mt-2 rounded-lg border text-[11px] p-2 ${res.sent > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="push-test-result" data-sent={res.sent}>
          <div className="font-bold flex items-center gap-1">{res.sent > 0 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {ta ? `Server அனுப்பியது: ${res.sent}/${res.devices} device` : `Server delivered: ${res.sent}/${res.devices} device(s)`}{res.failed > 0 ? ` · ${res.failed} failed` : ""}
          </div>
          {arrived && <div className="mt-1 font-bold text-emerald-700" data-testid="push-test-arrived">✅ {ta ? "இந்த phone-ல் notification வந்தது!" : "Notification arrived on this phone!"}</div>}
          {!arrived && res.sent > 0 && <div className="mt-1 opacity-80">{ta ? "Notification tray-ஐ பாருங்கள். வரவில்லையா? Phone Settings → Apps → Chrome → Notifications ON · Battery saver OFF." : "Check your notification tray. Not there? Phone Settings → Apps → Chrome → Notifications ON · Battery saver OFF."}</div>}
          {res.results?.filter((r) => !r.ok).map((r, i) => <div key={i} className="mt-1 text-red-700">• {r.device} ({r.endpoint_tail}) — {r.status ? `HTTP ${r.status}: ` : ""}{r.error}</div>)}
        </div>
      )}
    </div>
  );
};
